import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Breadcrumbs,
  TextInput,
  Select,
  Tabs,
  Table,
  CellText,
  Badge,
  Avatar,
  Menu,
  Pagination,
  Modal,
  Button,
  Alert,
  Spinner,
  EmptyState,
  SearchLg,
  Plus,
  DotsVertical,
  Edit01,
  Trash01,
} from '@opendate-app/setlist';
const qs = require('qs');

import CrmPageLayout from '../../shared/components/CrmPageLayout';
import DealFormModal from './DealFormModal';
import crmLabels from '../../shared/utils/crmLabels';
import DealsPipelineBoard from './DealsPipelineBoard';
import {
  getStageBadgeConfig,
  getStageBadgeColor,
  getTypeLabel,
  getTypeBadgeColor,
  getStatusConfig,
  statusOptions,
  formatAmount,
  formatActivity,
  formatRelativeActivity,
  initialsFor,
} from './dealStages';
import DealsFilterBar from './DealsFilterBar';
import DealsPipelineSummary, { countLabel } from './DealsPipelineSummary';
import DealsReportView from './DealsReportView';
import useDealPipelineReport from './useDealPipelineReport';
import { DEAL_FILTER_GROUPS, DEAL_FILTER_RANSACK_KEYS } from '../constants/dealFiltersConfig';

const VIEW_MODES = ['list', 'pipeline', 'report'];

const SORT_OPTIONS = [
  { value: '', label: 'Default order' },
  { value: 'last_activity_desc', label: 'Newest activity' },
  { value: 'last_activity_asc', label: 'Oldest activity' },
  { value: 'amount_desc', label: 'Highest amount' },
  { value: 'amount_asc', label: 'Lowest amount' },
  { value: 'close_date_asc', label: 'Soonest close date' },
  { value: 'name_asc', label: 'Name A–Z' },
];

function DealsPage({ team, csrfToken, stages = {}, types = [], statuses, owners = [], currentOwnerId = null, campaigns = [], funds = [], customFieldDefinitions = [] }) {
  const labels = crmLabels(team);
  const dealSingularLower = labels.deal.singular.toLowerCase();
  const dealPluralLower = labels.deal.plural.toLowerCase();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  const [organizations, setOrganizations] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);

  const [dealToRemove, setDealToRemove] = useState(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const [viewMode, setViewMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    return VIEW_MODES.includes(view) ? view : 'list';
  });
  const [boardCount, setBoardCount] = useState(null);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const [boardCategory, setBoardCategory] = useState('sales');

  const changeViewMode = (mode) => {
    setViewMode(mode);
    const params = new URLSearchParams(window.location.search);
    if (mode === 'pipeline' || mode === 'report') {
      params.set('view', mode);
    } else {
      params.delete('view');
    }
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
  };

  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchDropdownData = async () => {
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      try {
        const [orgsResponse, tagsResponse] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/organizations.json`, { params: { per_page: 500 } }),
          axios.get(`/teams/${team.id}/crm/tags`, { params: { scope: 'Deal' } }),
        ]);

        const orgs = (orgsResponse.data.organizations || []).map((org) => ({ id: org.id, name: org.name }));
        setOrganizations(orgs);
        setAvailableTags(tagsResponse.data || []);
      } catch (e) {
        toast.error('Failed to load dropdown data.');
      }
    };

    fetchDropdownData();
  }, [team && team.id, csrfToken]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters]);

  const enrichedFilterGroups = useMemo(() => {
    const stageOptions = Array.from(new Set(Object.values(stages || {}).flat()))
      .map((stage) => ({ value: stage, label: getStageBadgeConfig(stage).label }));
    const tagOptions = (availableTags || []).map((tag) => ({ value: String(tag.id), label: tag.name }));

    return DEAL_FILTER_GROUPS.map((group) => ({
      ...group,
      label: group.id === 'deal' ? labels.deal.singular : group.label,
      filters: group.filters.map((filter) => {
        if (filter.id === 'stage') return { ...filter, options: stageOptions };
        if (filter.id === 'status') return { ...filter, options: statusOptions(statuses) };
        if (filter.id === 'owner') return { ...filter, options: owners.map((o) => ({ value: String(o.value), label: o.label })) };
        if (filter.id === 'tags') return { ...filter, options: tagOptions };
        if (filter.id === 'giving_campaign') return { ...filter, options: campaigns.map((c) => ({ value: String(c.value), label: c.label })) };
        return filter;
      }),
    }));
  }, [stages, statuses, owners, campaigns, availableTags, labels]);

  const buildFilterRansack = () => {
    const q = {};
    if (searchTerm) q.name_cont = searchTerm;
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (!Array.isArray(value) || value.length === 0) return;
      const ransackKey = DEAL_FILTER_RANSACK_KEYS[key];
      if (ransackKey) q[ransackKey] = value;
    });
    return q;
  };

  const showsPipelineReport = viewMode === 'list' || viewMode === 'report';
  const pipelineReport = useDealPipelineReport({
    teamId: team && team.id,
    query: buildFilterRansack(),
    refreshKey: boardRefreshKey,
    enabled: showsPipelineReport,
    totalsOnly: viewMode === 'list',
  });

  useEffect(() => {
    if (!team || !team.id) return undefined;
    if (viewMode !== 'list') return undefined;

    let ignore = false;

    const fetchDeals = async () => {
      try {
        setLoading(true);
        setError(null);

        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = {
          page: currentPage,
          per_page: perPage,
        };
        if (sortOrder) params.sort = sortOrder;

        const q = buildFilterRansack();
        if (Object.keys(q).length > 0) params.q = q;

        const response = await axios.get(`/teams/${team.id}/crm/deals.json`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
        });
        if (ignore) return;
        const data = response.data || {};
        const dealsFromResponse = data.deals || [];
        const pagination = data.pagination || {};

        setDeals(dealsFromResponse);
        setTotalEntries(pagination.total_entries || dealsFromResponse.length || 0);
        setTotalPages(pagination.total_pages || 1);
      } catch (e) {
        if (ignore) return;
        const raw = e.response && e.response.data;
        let friendlyMessage = `Failed to load ${dealPluralLower}`;

        if (Array.isArray(raw)) {
          friendlyMessage = raw.join(', ');
        } else if (typeof raw === 'string' && raw.length < 500 && !raw.trim().startsWith('<')) {
          friendlyMessage = raw;
        }

        setError(friendlyMessage);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchDeals();
    return () => { ignore = true; };
  }, [team && team.id, csrfToken, currentPage, perPage, searchTerm, activeFilters, sortOrder, viewMode]);

  const reportOpenCount = pipelineReport.report ? Number(pipelineReport.report.totals.open_count || 0) : 0;
  const displayCount = (() => {
    if (viewMode === 'pipeline' && boardCount != null) return boardCount;
    if (viewMode === 'report') return reportOpenCount;
    return totalEntries;
  })();
  const resultCountLabel = (() => {
    if (viewMode !== 'report') return countLabel(displayCount, labels);
    return pipelineReport.report ? `${countLabel(displayCount, labels)} open` : '';
  })();
  const subtitle = 'Track opportunities across your pipeline. Switch to the pipeline view to manage cultivation by stage.';
  const dealsBreadcrumb = (
    <Breadcrumbs
      items={[
        { label: 'CRM', href: `/teams/${team.id}/crm_dashboard` },
        { label: labels.deal.plural },
      ]}
    />
  );

  const openCreateForm = () => {
    setEditingDeal(null);
    setShowForm(true);
  };

  // Deep-link support: the CRM dashboard's New deal button lands here with
  // ?action=new and expects the create form to open on arrival.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') !== 'new') return;
    openCreateForm();
    params.delete('action');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
  }, []);

  const openEditForm = (deal) => {
    setEditingDeal(deal);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDeal(null);
  };

  const handleDealSaved = (savedDeal, isNew) => {
    if (isNew) {
      setDeals((prev) => [savedDeal, ...prev]);
      setTotalEntries((prev) => (prev || 0) + 1);
    } else {
      setDeals((prev) => prev.map((d) => (d.id === savedDeal.id ? savedDeal : d)));
    }
    setBoardRefreshKey((prev) => prev + 1);
  };

  const handleOrganizationCreate = (newOrg) => {
    setOrganizations((prev) => [...prev, { id: newOrg.id, name: newOrg.name }].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleTagCreate = (newTag) => {
    setAvailableTags((prev) => [...prev, { id: newTag.id, name: newTag.name }].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const closeRemoveModal = () => {
    setDealToRemove(null);
    setRemoveSubmitting(false);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!dealToRemove || !team || !team.id || removeSubmitting) return;

    try {
      setRemoveSubmitting(true);
      setRemoveError(null);
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      await axios.delete(`/teams/${team.id}/crm/deals/${dealToRemove.id}.json`);

      setDeals((prev) => prev.filter((d) => d.id !== dealToRemove.id));
      setTotalEntries((prev) => Math.max((prev || 1) - 1, 0));
      setBoardRefreshKey((prev) => prev + 1);
      closeRemoveModal();
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setRemoveError(resp.data.join(', '));
      } else {
        setRemoveError(`Failed to delete ${dealSingularLower}`);
      }
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'name',
      header: `${labels.deal.singular} Name`,
      primary: true,
      render: (deal) => (
        <CellText
          primary={(
            <Button as="a" href={`/teams/${team.id}/crm/deals/${deal.id}`} variant="link-color">
              {deal.name}
            </Button>
          )}
          supporting={deal.organization_name || deal.primary_fan_name || undefined}
        />
      ),
    },
    {
      key: 'type',
      header: 'Category',
      render: (deal) => (deal.deal_type
        ? <Badge color={getTypeBadgeColor(deal.deal_type)} dot>{getTypeLabel(deal.deal_type, types)}</Badge>
        : '—'),
    },
    {
      key: 'stage',
      header: 'Stage',
      render: (deal) => <Badge color={getStageBadgeColor(deal.stage)} dot>{getStageBadgeConfig(deal.stage).label}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (deal) => (deal.status
        ? <Badge color={getStatusConfig(deal.status).color} dot>{getStatusConfig(deal.status).label}</Badge>
        : '—'),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (deal) => formatAmount(deal.amount),
    },
    {
      key: 'weighted_amount',
      header: 'Weighted',
      align: 'right',
      render: (deal) => formatAmount(deal.weighted_amount),
    },
    {
      key: 'campaign',
      header: 'Campaign',
      render: (deal) => deal.giving_campaign_name || '—',
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (deal) => (deal.owner_name
        ? <Avatar size="sm" src={deal.owner_avatar_url} initials={initialsFor(deal.owner_name)} alt={deal.owner_name} />
        : '—'),
    },
    {
      key: 'last_activity',
      header: 'Last Activity',
      render: (deal) => <span title={formatActivity(deal.updated_at)}>{formatRelativeActivity(deal.updated_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '48px',
      render: (deal) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`${labels.deal.singular} actions`} />}
          items={[
            { label: `Edit ${dealSingularLower}`, icon: Edit01, onClick: () => openEditForm(deal) },
            { label: `Delete ${dealSingularLower}`, icon: Trash01, destructive: true, onClick: () => setDealToRemove(deal) },
          ]}
        />
      ),
    },
  ], [team && team.id, types, labels]);

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="deals"
      mainTopMargin={32}
      breadcrumb={dealsBreadcrumb}
      title={labels.deal.plural}
      subtitle={subtitle}
      primaryAction={{
        label: `New ${dealSingularLower}`,
        iconClass: 'fa fa-plus',
        onClick: openCreateForm,
      }}
    >
      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-end crm-toolbar">
            <div className="flex-grow-1 mr-3 crm-toolbar-search" style={{ maxWidth: 360 }}>
              <TextInput
                leadingIcon={SearchLg}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Search ${dealPluralLower}`}
                aria-label={`Search ${dealPluralLower}`}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              />
            </div>

            <div className="d-flex align-items-center crm-toolbar-actions" style={{ gap: '12px' }}>
              {viewMode === 'list' && (
                <div style={{ width: 230 }}>
                  <Select
                    aria-label={`Sort ${dealPluralLower}`}
                    width={230}
                    options={SORT_OPTIONS}
                    value={sortOrder}
                    onChange={(value) => { setSortOrder(value || ''); setCurrentPage(1); }}
                  />
                </div>
              )}

              <Tabs
                variant="button-border"
                size="sm"
                aria-label="View mode"
                collapseBelow={0}
                value={viewMode}
                onChange={changeViewMode}
                items={[
                  { value: 'list', label: 'List' },
                  { value: 'pipeline', label: 'Pipeline' },
                  { value: 'report', label: 'Report' },
                ]}
              />
            </div>
          </div>

          {showsPipelineReport && (
            <div className="border-bottom px-4 py-3">
              <DealsFilterBar
                filterGroups={enrichedFilterGroups}
                activeFilters={activeFilters}
                onChange={setActiveFilters}
                resultCount={resultCountLabel}
              />
            </div>
          )}

          {showsPipelineReport && pipelineReport.report && (
            <div className="border-bottom px-4 py-3">
              <DealsPipelineSummary totals={pipelineReport.report.totals} labels={labels} loading={pipelineReport.loading} />
            </div>
          )}

          {viewMode === 'report' && (
            <div style={{ padding: '20px 24px' }}>
              <DealsReportView
                team={team}
                report={pipelineReport.report}
                loading={pipelineReport.loading}
                error={pipelineReport.error}
                labels={labels}
                types={types}
              />
            </div>
          )}

          {viewMode === 'pipeline' && (
            <div style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <Tabs
                  aria-label="Pipeline category"
                  collapseBelow={0}
                  value={boardCategory}
                  onChange={setBoardCategory}
                  items={[
                    { value: 'sales', label: 'Sales' },
                    { value: 'fundraising', label: 'Fundraising' },
                  ]}
                />
              </div>
              <DealsPipelineBoard
                team={team}
                csrfToken={csrfToken}
                stages={stages}
                category={boardCategory}
                types={types}
                searchTerm={searchTerm}
                refreshKey={boardRefreshKey}
                onCountChange={setBoardCount}
              />
            </div>
          )}

          {viewMode === 'list' && (
            <>
              <div style={{ padding: '4px 8px' }}>
                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner label={`Loading ${dealPluralLower}…`} />
                  </div>
                ) : error ? (
                  <div className="p-3">
                    <Alert color="error" title={`Failed to load ${dealPluralLower}`} description={error} />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    data={deals}
                    getRowKey={(deal) => deal.id}
                    empty={(
                      <EmptyState
                        size="sm"
                        icon={SearchLg}
                        title={`No ${labels.deal.plural.toLowerCase()} yet.`}
                        description={`Create your first ${dealSingularLower} to start tracking your pipeline.`}
                        primaryAction={{ label: `New ${dealSingularLower}`, icon: Plus, onClick: openCreateForm }}
                      />
                    )}
                  />
                )}
              </div>

              {!loading && !error && (
                <div className="px-4 py-3 border-top">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      if (!loading && page >= 1 && page <= totalPages) setCurrentPage(page);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DealFormModal
        isOpen={showForm}
        onClose={closeForm}
        deal={editingDeal}
        team={team}
        csrfToken={csrfToken}
        stages={stages}
        statuses={statuses}
        owners={owners}
        currentOwnerId={currentOwnerId}
        campaigns={campaigns}
        funds={funds}
        organizations={organizations}
        availableTags={availableTags}
        customFieldDefinitions={customFieldDefinitions}
        onOrganizationCreate={handleOrganizationCreate}
        onTagCreate={handleTagCreate}
        onSaved={handleDealSaved}
      />

      <Modal
        open={!!dealToRemove}
        onClose={closeRemoveModal}
        type="error"
        title={`Delete ${dealSingularLower}`}
        description="Are you sure? This cannot be undone."
        footer={(
          <>
            <Button variant="secondary" onClick={closeRemoveModal} disabled={removeSubmitting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmRemove} disabled={removeSubmitting}>
              {removeSubmitting ? 'Deleting…' : `Delete ${dealSingularLower}`}
            </Button>
          </>
        )}
      >
        {removeError && <Alert color="error" description={removeError} />}
      </Modal>
    </CrmPageLayout>
  );
}

export default DealsPage;
